import React, { useEffect, useState } from 'react';

import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import DoneIcon from '@material-ui/icons/Done';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import Typography from '@material-ui/core/Typography';

import * as StampCardService from '../../api/stampCardService';

const useStoreStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
    width: '100%',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
  },
  visited: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
    marginBottom: theme.spacing(2),
  },
  remaining: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginRight: 4,
  },
  subtitleBold: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightBold,
  },
  info: {
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(),
    padding: '0 16px',
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(),
  },
  actionButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  showDetailContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 16px',
  },
  showDetailButton: {
    color: theme.palette.text.secondary,
  },
  grow: {
    flexGrow: 1,
  },
  finePrint: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  storeDetailContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 16px',
  },
  done: {
    color: '#008000',
    stroke: '#008000',
    strokeWidth: 1,
  },
  pending: {
    color: theme.palette.primary.main,
  },
  storeContainer: {
    padding: '0 16px',
  },
  noStoreDetailContainer: {
    padding: '0',
  },
}));

export function StoreDetail({
  store,
  visited,
}: {
  store: StampCardService.StoreInfo;
  visited?: boolean;
}) {
  const classes = useStoreStyles();

  return (
    <div className={classes.storeDetailContainer} key={store.ID}>
      {visited ? (
        <DoneIcon fontSize="medium" className={classes.done} />
      ) : (
        <FiberManualRecordIcon fontSize="small" className={classes.pending} />
      )}
      <Typography variant="body2" className={classes.info}>
        <Grid container alignItems="center" spacing={1}>
          {visited && store.Count > 1 ? (
            <Grid item xs={9}>
              {store.Name}
            </Grid>
          ) : (
            <Grid item xs={12}>
              {store.Name}
            </Grid>
          )}
          {visited && store.Count > 1 && (
            <Grid item xs={3} style={{ whiteSpace: 'nowrap' }}>
              ({store.Count} times)
            </Grid>
          )}
        </Grid>
      </Typography>
    </div>
  );
}

export function VisitedStoreContainer({
  visitedStoreList,
  remainingStoreList,
  handleShowMoreDetails,
}: {
  visitedStoreList: StampCardService.StoreInfo[];
  remainingStoreList: StampCardService.StoreInfo[];
  handleShowMoreDetails: () => void;
}) {
  const classes = useStoreStyles();
  const [trimRemainingStoreList, setTrimRemainingStoreList] = useState<
    StampCardService.StoreInfo[]
  >([]);

  useEffect(() => {
    if (remainingStoreList.length > 5) {
      setTrimRemainingStoreList(remainingStoreList.slice(0, 5));
    } else {
      setTrimRemainingStoreList(remainingStoreList);
    }
  }, [visitedStoreList, remainingStoreList]);

  return (
    <div className={classes.root}>
      <div className={classes.cardContainer}>
        <div className={classes.storeContainer}>
          <Typography variant="h6" className={classes.visited}>
            Visited
          </Typography>
          {visitedStoreList.length === 0 && (
            <div className={classes.noStoreDetailContainer}>
              <Typography variant="body2" className={classes.info}>
                Visit one of the locations below!
              </Typography>
            </div>
          )}
          <>
            {visitedStoreList.map((store) => (
              <StoreDetail key={store.ID} store={store} visited />
            ))}
          </>
        </div>
        {remainingStoreList.length === 0 && (
          <div className={classes.storeContainer}>
            <Typography variant="h6" className={classes.remaining}>
              Remaining
            </Typography>
            <div className={classes.noStoreDetailContainer}>
              <Typography variant="body2" className={classes.info}>
                All locations visited!
              </Typography>
            </div>
          </div>
        )}
        {remainingStoreList.length > 0 && (
          <div className={classes.storeContainer}>
            <Typography variant="h6" className={classes.remaining}>
              Remaining
            </Typography>
            <>
              {trimRemainingStoreList &&
                trimRemainingStoreList.map((store) => (
                  <StoreDetail key={store.ID} store={store} />
                ))}
              {/* {showDetails &&
                remainingStoreList.map((store) => (
                  <StoreDetail key={store.ID} store={store} />
                ))} */}
            </>
          </div>
        )}
        {remainingStoreList.length > 5 && (
          <div className={classes.actionContainer}>
            <Button
              className={classes.actionButton}
              onClick={handleShowMoreDetails}
              size="large"
            >
              {'View More'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

type Props = {
  storeVisitedList: StampCardService.StoreVisitList;
  handleShowMoreDetails: () => void;
};

export default function VisitedStoresCard({
  storeVisitedList,
  handleShowMoreDetails,
}: Props) {
  const classes = useStyles();

  return (
    <Grid item xs={12} className={classes.root}>
      <VisitedStoreContainer
        visitedStoreList={storeVisitedList.visitedStores}
        remainingStoreList={storeVisitedList.nonVisitedStores}
        handleShowMoreDetails={handleShowMoreDetails}
      />
    </Grid>
  );
}
